import { memo, useCallback } from 'react';

import filesize from 'filesize';
import { Check, X } from 'lucide-react';
import { defaultStyles, FileIcon } from 'react-file-icon';

import { Button } from '@/shadcn/button';
import { cn } from '@/shadcn/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/shadcn/tooltip';

import { getFileExtension } from '@/components/fileIcon/file.utils';
import { TextEllipsis } from '@/components/ui/utils/textEllipsis/textEllipsis';
import type { FileType } from '../types';
import { ProgressBar } from './progressBar/progressBar.fileUpload';

interface Props {
  file: FileType;
  maxFileSize: number;
  handleDeleteFile: (x: any) => void;
  disabled: boolean;
  fieldName: string;
}

export const File = memo(
  ({ file, handleDeleteFile, maxFileSize, disabled, fieldName }: Props) => {
    const { id, name, size, uploadPercent, errorType } = file;

    const isUploading = uploadPercent! >= 0;
    const fileExtension = getFileExtension(name);

    const renderSize = useCallback(() => {
      if (isUploading) {
        return <ProgressBar completed={uploadPercent} />;
      }
      return (
        <div
          className="flex items-center gap-2 text-[10px] text-muted-foreground uppercase"
          title={`File size: ${filesize(size)}`}
        >
          <span>{`${fileExtension}  ·  ${filesize(size, { round: 1 })}`}</span>
          <Check className="text-valid" size={16} />
        </div>
      );
    }, [isUploading, uploadPercent, size]);

    const renderError = useCallback(() => {
      switch (errorType) {
        case 'size':
          return (
            <div
              className="text-destructive"
              data-cy={`file-${fieldName}-error-size`}
              data-testid="file-error"
            >
              File size error: this file is over the {filesize(maxFileSize)}{' '}
              limit.
            </div>
          );
        case 'extension':
          return (
            <div
              className="text-destructive"
              data-cy={`file-${fieldName}-error-extension`}
              data-testid="file-error"
            >
              File type error:{' '}
              {`.${fileExtension} files are not supported in
                Trace.`}
            </div>
          );
        default:
          return (
            <div
              className="text-destructive"
              data-cy={`file-${fieldName}-error-default`}
              data-testid="file-error"
            >
              File error.
            </div>
          );
      }
    }, [errorType, maxFileSize, fileExtension]);

    return (
      <div
        className={cn(
          'flex w-full items-center justify-between gap-5 rounded-md border border-border px-4 py-2 text-foreground transition-colors hover:bg-muted',
          !!errorType && 'border-destructive text-destructive'
        )}
        data-cy="forms-js-file"
        key={id}
      >
        <div className="flex size-6 items-center justify-center rounded-sm">
          <FileIcon
            extension={fileExtension}
            {...defaultStyles[fileExtension]}
          />
        </div>

        <div className="flex grow flex-col flex-nowrap gap-1 overflow-hidden">
          <TextEllipsis
            className="grow overflow-ellipsis font-semibold text-foreground text-xs"
            middleEllipsis
            text={name}
          />

          <div className="grow text-[10px] text-muted-foreground">
            {errorType ? renderError() : renderSize()}
          </div>
        </div>
        {!disabled && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  aria-label="Delete file"
                  data-cy={`file-${fieldName}-${name}-delete-button`}
                  data-testid="delete-file-button"
                  onClick={() => handleDeleteFile(id)}
                  size="icon"
                  type="button"
                  variant="ghost"
                >
                  <X size={16} />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                Delete <b>{name}</b>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    );
  }
);
